import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Gift, Users, Copy, Mail, CheckCircle, Share2, 
  DollarSign, Clock, Loader2, Send, Trophy, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import ReferralLeaderboard from './ReferralLeaderboard';
import RewardHistory from './RewardHistory';

import { API_URL } from '../utils/apiConfig';

export default function ReferralProgram({ isOpen, onClose, token }) {
  const [referralCode, setReferralCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showRewardHistory, setShowRewardHistory] = useState(false);
  
  const { language } = useLanguage();
  
  const t = {
    en: {
      title: 'Referral Program',
      subtitle: 'Earn $5 for every friend who joins',
      yourCode: 'Your Referral Code',
      copyCode: 'Copy Code',
      copied: 'Copied!',
      shareLink: 'Share Link',
      inviteFriend: 'Invite a Friend',
      emailPlaceholder: 'friend@example.com',
      sendInvite: 'Send Invite',
      howItWorks: 'How it Works',
      step1: 'Share your code with friends',
      step2: 'They sign up using your code',
      step3: 'After their first transaction, you both get $5',
      yourStats: 'Your Stats',
      invitesSent: 'Invites Sent',
      signups: 'Sign Ups',
      qualified: 'Qualified',
      earned: 'Earned',
      pending: 'Pending',
      recentInvites: 'Recent Invites',
      statusPending: 'Pending',
      statusSignedUp: 'Signed Up',
      statusQualified: 'Qualified',
      statusCredited: 'Credited',
      noInvites: 'No invites sent yet',
      inviteSent: 'Invite sent successfully!',
      viewLeaderboard: 'View Leaderboard',
      viewRewards: 'My Rewards'
    },
    es: {
      title: 'Programa de Referidos',
      subtitle: 'Gana $5 por cada amigo que se una',
      yourCode: 'Tu Código de Referido',
      copyCode: 'Copiar Código',
      copied: '¡Copiado!',
      shareLink: 'Compartir Enlace',
      inviteFriend: 'Invitar a un Amigo',
      emailPlaceholder: 'amigo@ejemplo.com',
      sendInvite: 'Enviar Invitación',
      howItWorks: 'Cómo Funciona',
      step1: 'Comparte tu código con amigos',
      step2: 'Ellos se registran usando tu código',
      step3: 'Después de su primera transacción, ambos reciben $5',
      yourStats: 'Tus Estadísticas',
      invitesSent: 'Invitaciones Enviadas',
      signups: 'Registros',
      qualified: 'Calificados',
      earned: 'Ganado',
      pending: 'Pendiente',
      recentInvites: 'Invitaciones Recientes',
      statusPending: 'Pendiente',
      statusSignedUp: 'Registrado',
      statusQualified: 'Calificado',
      statusCredited: 'Acreditado',
      noInvites: 'Aún no has enviado invitaciones',
      inviteSent: '¡Invitación enviada exitosamente!',
      viewLeaderboard: 'Ver Clasificación',
      viewRewards: 'Mis Recompensas'
    }
  };
  
  const text = t[language] || t.en;
  
  useEffect(() => {
    if (isOpen && token) {
      fetchReferralData();
    }
  }, [isOpen, token]);
  
  const fetchReferralData = async () => {
    setLoading(true);
    try {
      // Get referral code
      const codeResponse = await fetch(`${API_URL}/api/referrals/my-code`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const codeData = await codeResponse.json();
      setReferralCode(codeData);
      
      // Get stats
      const statsResponse = await fetch(`${API_URL}/api/referrals/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsResponse.json();
      setStats(statsData);
      
      // Get invites
      const invitesResponse = await fetch(`${API_URL}/api/referrals/invites?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const invitesData = await invitesResponse.json();
      setInvites(invitesData.invites || []);
      
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const copyCode = () => {
    if (referralCode?.code) {
      navigator.clipboard.writeText(referralCode.code);
      toast.success(text.copied);
    }
  };
  
  const shareLink = () => {
    if (referralCode?.share_url) {
      if (navigator.share) {
        navigator.share({
          title: 'Join PeCashGlobal',
          text: language === 'es' 
            ? 'Únete a PeCashGlobal y obtén $5 de crédito con mi código de referido!'
            : 'Join PeCashGlobal and get $5 credit with my referral code!',
          url: referralCode.share_url
        });
      } else {
        navigator.clipboard.writeText(referralCode.share_url);
        toast.success(text.copied);
      }
    }
  };
  
  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/referrals/invite?email=${encodeURIComponent(inviteEmail)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send invite');
      }
      
      toast.success(text.inviteSent);
      setInviteEmail('');
      fetchReferralData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'credited': return 'bg-emerald-100 text-emerald-700';
      case 'qualified': return 'bg-blue-100 text-blue-700';
      case 'signed_up': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'credited': return text.statusCredited;
      case 'qualified': return text.statusQualified;
      case 'signed_up': return text.statusSignedUp;
      default: return text.statusPending;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-purple-600" />
            {text.title}
          </DialogTitle>
          <DialogDescription>{text.subtitle}</DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Referral Code */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
              <p className="text-purple-100 text-sm mb-2">{text.yourCode}</p>
              <div className="flex items-center gap-3">
                <span className="font-mono text-2xl font-bold tracking-wider">
                  {referralCode?.code || '---'}
                </span>
                <Button 
                  size="sm" 
                  variant="secondary" 
                  onClick={copyCode}
                  className="bg-white/20 hover:bg-white/30 text-white"
                >
                  <Copy className="w-4 h-4 mr-1" /> {text.copyCode}
                </Button>
              </div>
              <Button 
                size="sm" 
                variant="secondary" 
                onClick={shareLink}
                className="mt-3 bg-white/20 hover:bg-white/30 text-white"
              >
                <Share2 className="w-4 h-4 mr-1" /> {text.shareLink}
              </Button>
            </div>
            
            {/* How it Works */}
            <div className="bg-slate-50 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 mb-3">{text.howItWorks}</h3>
              <div className="space-y-3">
                {[text.step1, text.step2, text.step3].map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <p className="text-sm text-slate-600">{step}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Invite Friend */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">{text.inviteFriend}</h3>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder={text.emailPlaceholder}
                  className="flex-1"
                  data-testid="invite-email-input"
                />
                <Button 
                  onClick={sendInvite}
                  disabled={sending || !inviteEmail.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="send-invite-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <Send className="w-4 h-4 mr-1" /> {text.sendInvite}
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Stats */}
            {stats && (
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">{text.yourStats}</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                    <Users className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-slate-900">{stats.signups || 0}</p>
                    <p className="text-xs text-slate-500">{text.signups}</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-slate-900">{stats.qualified || 0}</p>
                    <p className="text-xs text-slate-500">{text.qualified}</p>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-lg p-3 text-center">
                    <DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                    <p className="text-xl font-bold text-emerald-600">${stats.credits_earned || 0}</p>
                    <p className="text-xs text-slate-500">{text.earned}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Recent Invites */}
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">{text.recentInvites}</h3>
              {invites.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">{text.noInvites}</p>
              ) : (
                <div className="space-y-2">
                  {invites.map((invite, i) => (
                    <div key={i} className="flex items-center justify-between bg-white border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700">{invite.email}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(invite.status)}`}>
                        {getStatusText(invite.status)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* View Leaderboard Button */}
            <div className="mt-6 pt-4 border-t border-slate-200 flex gap-3">
              <Button
                onClick={() => setShowLeaderboard(true)}
                variant="outline"
                className="flex-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                data-testid="view-leaderboard-btn"
              >
                <Trophy className="w-4 h-4 mr-2" />
                {text.viewLeaderboard}
              </Button>
              <Button
                onClick={() => setShowRewardHistory(true)}
                variant="outline"
                className="flex-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                data-testid="view-rewards-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {text.viewRewards}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
      
      {/* Leaderboard Modal */}
      <ReferralLeaderboard 
        token={token}
        isOpen={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
      />
      
      {/* Reward History Modal */}
      <RewardHistory 
        token={token}
        isOpen={showRewardHistory}
        onClose={() => setShowRewardHistory(false)}
      />
    </Dialog>
  );
}
